import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Table } from 'react-bootstrap'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import api from 'api/api'
import { toast } from 'react-toastify'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { getServerDate, startOfMonth, endOfMonth } from 'utils/date'
import { LoadingSpinner } from 'components/LoadingSpinner'
import { DateRangeSelector } from 'components/DateRangeSelector'

const COLORS = ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6f42c1', '#0dcaf0', '#fd7e14', '#20c997', '#6610f2', '#d63384']

const CategoriasMasVendidas = () => {
	const [loading, setLoading] = useState(false)
	const [fechaInicio, setFechaInicio] = useState(getServerDate(startOfMonth()))
	const [fechaFin, setFechaFin] = useState(getServerDate(endOfMonth()))
	const [datos, setDatos] = useState(null)

	const cargarDatos = async (inicio, fin) => {
		try {
			setLoading(true)
			const response = await api.get(`a/reportes/categorias-mas-vendidas?fechaInicio=${inicio}&fechaFin=${fin}`)

			if (response.error) {
				toast.error(response.msg || 'Error al cargar datos')
				return
			}

			setDatos(response.datos)

		} catch (error) {
			console.error('Error:', error)
			toast.error('Error al cargar el reporte')
		} finally {
			setLoading(false)
		}
	}

	useEffect(() => {
		cargarDatos(fechaInicio, fechaFin)
	}, [])

	const handleRangeChange = (inicio, fin) => {
		setFechaInicio(inicio)
		setFechaFin(fin)
	}

	const handleConsultar = (inicio, fin) => {
		cargarDatos(inicio, fin)
	}

	const formatMonto = (value) => {
		return formatNumberForDisplay(value.toString()) + ' Gs.'
	}

	const CustomTooltip = ({ active, payload }) => {
		if (active && payload && payload.length) {
			const data = payload[0].payload
			return (
				<div className="bg-white p-2 border rounded shadow-sm">
					<p className="mb-1 fw-bold">{data.nombre}</p>
					<p className="mb-0 small">{formatMonto(data.montoTotal)}</p>
					<p className="mb-0 small text-muted">{data.porcentaje}%</p>
				</div>
			)
		}
		return null
	}

	return (
		<div className="categorias-mas-vendidas">
			<DateRangeSelector
				fechaInicio={new Date(fechaInicio.replace(/-/g, '/'))}
				fechaFin={new Date(fechaFin.replace(/-/g, '/'))}
				onRangeChange={handleRangeChange}
				onConsultar={handleConsultar}
			/>

			{loading ? (
				<div className="d-flex justify-content-center py-5">
					<LoadingSpinner />
				</div>
			) : datos && datos.categorias && datos.categorias.length > 0 ? (
				<Row>
					<Col lg={5} className="mb-4">
						<Card className="h-100">
							<Card.Header>
								<h5 className="mb-0">
									<i className="fa fa-chart-pie me-2 text-primary"></i>
									Distribucion por categoria
								</h5>
							</Card.Header>
							<Card.Body>
								<ResponsiveContainer width="100%" height={300}>
									<PieChart>
										<Pie
											data={datos.categorias}
											cx="50%"
											cy="50%"
											innerRadius={60}
											outerRadius={100}
											paddingAngle={2}
											dataKey="montoTotal"
											nameKey="nombre"
										>
											{datos.categorias.map((entry, index) => (
												<Cell 
													key={`cell-${index}`} 
													fill={COLORS[index % COLORS.length]} 
												/>
											))}
										</Pie>
										<Tooltip content={<CustomTooltip />} />
									</PieChart>
								</ResponsiveContainer>

								<div className="mt-3">
									{datos.categorias.slice(0, 6).map((cat, index) => (
										<div key={cat.id} className="d-flex align-items-center gap-2 mb-2">
											<div 
												className="rounded" 
												style={{ 
													width: '14px', 
													height: '14px', 
													backgroundColor: COLORS[index % COLORS.length] 
												}}
											></div>
											<span className="flex-grow-1 small">{cat.nombre}</span>
											<span className="fw-bold small">{cat.porcentaje}%</span>
										</div>
									))}
									{datos.categorias.length > 6 && (
										<div className="text-muted small text-center mt-2">
											+{datos.categorias.length - 6} categorias mas
										</div>
									)}
								</div>
							</Card.Body>
						</Card>
					</Col>

					<Col lg={7} className="mb-4">
						<Card className="h-100">
							<Card.Header className="d-flex justify-content-between align-items-center">
								<h5 className="mb-0">
									<i className="fa fa-list me-2 text-primary"></i>
									Detalle por categoria
								</h5>
								<span className="badge bg-primary">
									Total: {formatMonto(datos.totalMonto)}
								</span>
							</Card.Header>
							<Card.Body className="p-0">
								<Table hover responsive className="mb-0">
									<thead className="table-light">
										<tr>
											<th>Categoria</th>
											<th className="text-end">Items</th>
											<th className="text-end">Monto</th>
											<th className="text-end" style={{ width: '150px' }}>%</th>
										</tr>
									</thead>
									<tbody>
										{datos.categorias.map((cat, index) => (
											<tr key={cat.id}>
												<td>
													<div className="d-flex align-items-center gap-2">
														<div 
															className="rounded" 
															style={{ 
																width: '10px', 
																height: '10px', 
																backgroundColor: COLORS[index % COLORS.length] 
															}}
														></div>
														<span className="fw-medium">{cat.nombre}</span>
													</div>
												</td>
												<td className="text-end">{cat.cantidadItems}</td>
												<td className="text-end fw-bold text-success">
													{formatMonto(cat.montoTotal)}
												</td>
												<td className="text-end">
													<div className="d-flex align-items-center justify-content-end gap-2">
														<div 
															className="progress flex-grow-1" 
															style={{ height: '8px', maxWidth: '80px' }}
														>
															<div 
																className="progress-bar"
																style={{ 
																	width: `${cat.porcentaje}%`,
																	backgroundColor: COLORS[index % COLORS.length]
																}}
															></div>
														</div>
														<span className="fw-bold" style={{ minWidth: '45px' }}>
															{cat.porcentaje}%
														</span>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			) : (
				<div className="text-center text-muted py-5">
					<i className="fa fa-tags fa-3x mb-3"></i>
					<p>No hay datos de categorias para el periodo seleccionado</p>
				</div>
			)}
		</div>
	)
}

export default CategoriasMasVendidas